import { useEffect, useRef, useState } from 'react';

// How the advisor moves around the meeting: the list of pages, the numbered
// whiteboards, and, on a phone or a tablet, the slim bar that carries them.
//
// A laptop has room for the pages as a column down the left of the screen. A
// phone does not: that column wrapped into rows of buttons and took half the
// screen or more, and the page itself ran off the side (Jimmy, 2026-10-04,
// from an iPhone). So on a touch screen, and in any small window, the column
// becomes one slim bar: the menu, a pen, Back and Next, the page you are on
// (tap it for the list), and the whiteboards. It runs across the top when the
// screen is taller than the page's own shape and down the left when it is
// wider, which is whichever costs the page less room.

// Touch screens (an iPad or a phone in either orientation) and windows too
// small for the column. The same switch is read in CSS through the
// `is-compact` class this decides, so the two cannot disagree.
export const COMPACT_QUERY = '(pointer: coarse), (max-width: 900px), (max-height: 560px)';

export function useCompactLayout(query = COMPACT_QUERY) {
  const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false);
  const [compact, setCompact] = useState(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const update = () => setCompact(list.matches);
    update();
    if (list.addEventListener) list.addEventListener('change', update);
    else list.addListener(update);
    return () => {
      if (list.removeEventListener) list.removeEventListener('change', update);
      else list.removeListener(update);
    };
  }, [query]);
  return compact;
}

function PenIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 20l4.4-1L19 8.4a2.1 2.1 0 0 0-3-3L5.4 16 4 20z" />
      <path d="M14.5 7l2.5 2.5" />
    </svg>
  );
}

const CHEVRONS = { left: 'M15 5l-7 7 7 7', right: 'M9 5l7 7-7 7', down: 'M6 9l6 6 6-6' };

/** A page with its corner turned: the takeaway. */
export function TakeawayIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </svg>
  );
}

function Chevron({ dir }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d={CHEVRONS[dir]} />
    </svg>
  );
}

/**
 * Every page of the meeting, in its two parts. The parts are told apart by a
 * line and by the quieter look of the second, not by headings (Jimmy,
 * 2026-10-05); their names are still read out to a screen reader.
 * `after(groupKey)` adds what belongs at the end of a part, which is the
 * whiteboards under Illustrations.
 */
export function StepList({ steps, groups, current, onPick, after = null }) {
  return (
    <nav className="conv-steps" aria-label="Conversation">
      {groups.map(g => (
        <div className={`conv-steps-group is-${g.key}`} role="group" aria-label={g.label} key={g.key}>
          {steps.map((s, i) => (s.group !== g.key ? null : (
            <button
              key={s.key}
              type="button"
              aria-current={i === current ? 'step' : undefined}
              disabled={!s.available}
              aria-disabled={!s.available ? 'true' : undefined}
              title={s.available ? undefined : s.reason}
              onClick={() => { if (s.available) onPick(i); }}
            >
              <span>{s.n}</span>
              {s.label}
            </button>
          )))}
          {after ? after(g.key) : null}
        </div>
      ))}
    </nav>
  );
}

/** The whiteboards, by number, and a way to start another. */
export function BoardChips({ boards, active, onPick, onAdd, canAdd }) {
  return (
    <div className="conv-boards" role="group" aria-label="Whiteboards">
      {boards.map((k, n) => (
        <button
          key={k}
          type="button"
          className="conv-board-chip"
          aria-pressed={k === active}
          aria-label={`Whiteboard ${n + 1}`}
          onClick={() => onPick(k)}
        >
          {n + 1}
        </button>
      ))}
      <button
        type="button"
        className="conv-board-chip is-add"
        aria-label="New whiteboard"
        title="New whiteboard"
        disabled={!canAdd}
        onClick={onAdd}
      >
        +
      </button>
    </div>
  );
}

/**
 * The page you are on, and the whole list a tap away. It closes on a choice,
 * on a tap anywhere else, and on Escape, like the meeting menu.
 */
function StepPicker({ steps, groups, current, onPick, onTakeaway }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const here = steps[current] || steps[0];

  useEffect(() => {
    if (!open) return undefined;
    const outside = e => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    const key = e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);

  return (
    <div className="conv-picker" ref={wrap}>
      <button
        type="button"
        className="conv-picker-btn"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Page: ${here.label}`}
        onClick={() => setOpen(v => !v)}
      >
        <span className="conv-picker-n">{here.n}</span>
        <span className="conv-picker-label">{here.label}</span>
        <Chevron dir="down" />
      </button>
      {open ? (
        <div className="conv-picker-panel">
          <StepList steps={steps} groups={groups} current={current} onPick={i => { setOpen(false); onPick(i); }} />
          {onTakeaway ? (
            <button type="button" className="conv-picker-takeaway" onClick={() => { setOpen(false); onTakeaway(); }}>
              <TakeawayIcon />
              Review takeaway
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The meeting's controls on a phone or a tablet when the meeting is the brief
 * beside the whiteboard (Jimmy, 2026-10-15): the menu, which of the two to
 * show since there is room for one, the whiteboards by number, and the
 * takeaway. An iPad joined as a companion only draws, so it has no switch.
 */
export function SplitBar({ menu, view, onView = null, boards = null, onTakeaway = null, link = null, pen = null }) {
  return (
    <aside className="conv-bar no-print" aria-label="Meeting">
      <div className="conv-bar-group is-tools">
        {menu}
        {/* The marker for the brief, while the brief is the side shown. */}
        {pen && view === 'brief' ? (
          <button
            type="button"
            className="conv-bar-btn is-pen"
            aria-label="Draw on the brief"
            aria-pressed={pen.on}
            disabled={pen.disabled}
            onClick={pen.onToggle}
          >
            <PenIcon />
          </button>
        ) : null}
      </div>
      {onView ? (
        <div className="conv-bar-group conv-bar-views" role="group" aria-label="Show">
          <button type="button" className="conv-bar-btn is-view" aria-pressed={view === 'brief'} onClick={() => onView('brief')}>Brief</button>
          <button type="button" className="conv-bar-btn is-view" aria-pressed={view === 'board'} onClick={() => onView('board')}>Whiteboard</button>
        </div>
      ) : null}
      {view === 'board' ? boards : null}
      {onTakeaway ? (
        <button type="button" className="conv-bar-btn is-takeaway" aria-label="Review takeaway" onClick={onTakeaway}>
          <TakeawayIcon />
          <span className="conv-bar-btn-label" aria-hidden="true">Takeaway</span>
        </button>
      ) : null}
      {link ? <p className="conv-link-status sr-only" role="status" aria-live="polite">{link.text}</p> : null}
    </aside>
  );
}

/**
 * The meeting's controls on a phone, a tablet or a small window.
 *
 * `menu` is the meeting menu itself. `pen` picks the marker up and puts it
 * down, which a laptop does with D; on a whiteboard it is always in hand.
 * `onTakeaway` opens the takeaway: a button at the end of the bar where there
 * is room for one, and always the last line of the page list.
 * `boards` is the whiteboard numbers, shown only on a whiteboard. `link` says
 * whether the other device is there: it is read out to a screen reader here,
 * shown as a dot on the menu button, and, when the meeting screen is gone,
 * said in words above the page.
 */
export function CompactBar({ menu, steps, groups, current, onPick, prevLabel, nextLabel, onBack, onNext, onTakeaway, pen, boards = null, link = null }) {
  return (
    <aside className="conv-bar no-print" aria-label="Meeting">
      <div className="conv-bar-group is-tools">
        {menu}
        <button
          type="button"
          className="conv-bar-btn is-pen"
          aria-label="Draw"
          aria-pressed={pen.on}
          disabled={pen.disabled}
          title={pen.title}
          onClick={pen.onToggle}
        >
          <PenIcon />
        </button>
      </div>
      <div className="conv-bar-group is-nav">
        <button
          type="button"
          className="conv-bar-btn is-back"
          aria-label={prevLabel ? `Back: ${prevLabel}` : 'Back'}
          disabled={!prevLabel}
          onClick={onBack}
        >
          <Chevron dir="left" />
        </button>
        <StepPicker steps={steps} groups={groups} current={current} onPick={onPick} onTakeaway={onTakeaway} />
        <button type="button" className="conv-bar-btn is-next" aria-label={`Next: ${nextLabel}`} onClick={onNext}>
          <Chevron dir="right" />
        </button>
      </div>
      {boards}
      <button type="button" className="conv-bar-btn is-takeaway" aria-label="Review takeaway" onClick={onTakeaway}>
        <TakeawayIcon />
        <span className="conv-bar-btn-label" aria-hidden="true">Takeaway</span>
      </button>
      {link ? <p className="conv-link-status sr-only" role="status" aria-live="polite">{link.text}</p> : null}
    </aside>
  );
}
